<template>
  <div>
    <v-skeleton-loader
      type="table-thead, table-tbody"
      width="100%"
      :loading="marketTableLoader"
      class="pa-4"
      boilerplate
    >
      <v-container class="prices-list-table pa-0 fill-height" fluid>
        <v-card width="100%" class="fill-height" color="gray10 pa-5">
          <v-row no-gutters class="justify-md-space-between justify-center">
            <v-col cols="12" md="9" class="order-2 order-md-1 mt-md-0 mt-4">
              <v-tabs
                class="market-tabs"
                height="38"
                background-color="transparent"
                :show-arrows="false"
              >
                <v-tabs-slider></v-tabs-slider>
                <v-tab @click="changeFavorTab" v-if="isAuth">
                  {{ $t("market.custom") }}
                </v-tab>
                <v-tab
                  v-for="(item, index) in baseCoinType"
                  :key="index"
                  @click="changeTab(item)"
                >
                  {{
                    item.coinType === "VIRTUAL"
                      ? $t("marketsPage.demoMarket")
                      : item.localedUnit
                  }}
                </v-tab>
              </v-tabs>
            </v-col>
            <v-col cols="12" md="3" sm="8" class="order-1 order-md-2">
              <v-text-field
                v-model="searchKey"
                :placeholder="$t('common.searchPlaceHolder')"
                solo
                flat
                hide-details
                dense
                class="custom-table-input"
                height="38"
                full-width
                clearable
                @input="handleSearch"
                @click:clear="searchKey = ''"
                clear-icon="$deleteSearch"
                background-color="field"
              >
                <template v-slot:prepend-inner>
                  <v-icon class="ms-2" size="24" :clr="'var(--v-gray1-base)'"
                    >$search</v-icon
                  >
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" class="order-3 mt-5">
              <markets-table
                :items="items"
                :current-tab="currentTab"
                @getFavor="getFavor"
                @updateItems="updateItems"
              />
              <v-pagination
                v-if="showPagination"
                class="custom-number-pagination"
                v-model="pageNo"
                :length="totalPages"
                total-visible="6"
                @input="getSymbol"
              ></v-pagination>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </v-skeleton-loader>
  </div>
</template>

<script>
import { ExchangeService, MarketMarketService } from "@/services";
import debounce from "lodash-es/debounce";
import MarketsTable from "@/components/Market/markets/marketsTable.vue";
import { authMixin } from "@/mixins/authMixin";

const Stomp = require("stompjs");
const SockJS = require("sockjs-client");

export default {
  mixins: [authMixin],
  components: {
    MarketsTable
  },
  data() {
    return {
      subscriptions: [],
      marketTableLoader: false,
      items: [],
      favoriteList: [],
      searchKey: "",
      currentTab: null,
      baseCoinType: [],
      showPagination: false,
      pageNo: 1,
      pageSize: 10,
      totalPages: 0
    };
  },
  mounted() {
    this.getBaseSymbols();
    this.isAuth && this.getFavor();
  },
  created() {
    // Create debounced methods in created hook to ensure proper 'this' binding
    this.debounceSearchFavors = debounce(this.getFavor, 500);
    this.debounceSearch = debounce(this.getSymbol, 500);
  },
  methods: {
    updateItems({ item, value }) {
      this.items = this.items.map(obj => {
        if (obj.symbol === item.symbol) {
          return { ...obj, isFavor: value };
        }
        return obj;
      });
    },
    changeFavorTab() {
      this.searchKey = "";
      this.currentTab = null;
      this.getFavor();
    },
    changeTab(item) {
      this.showPagination = true;
      this.pageNo = 1;
      this.pageSize = 10;
      this.currentTab = item;
      this.searchKey = "";
      this.getSymbol();
    },
    handleSearch() {
      if (!this.currentTab) {
        this.debounceSearchFavors();
      } else {
        this.debounceSearch();
      }
    },
    debounceSearchFavors: debounce(() => {
      this.getFavor();
    }, 500),
    debounceSearch: debounce(() => {
      this.getSymbol();
    }, 500),
    getFavor() {
      this.showPagination = false;
      this.items = [];
      this.favoriteList = [];
      ExchangeService.getFavoredMarkets({ symbol: this.searchKey }).then(
        response => {
          if (response.data.data?.length > 0) {
            this.items = response.data.data;
            this.items.forEach(item => {
              item.isFavor = true;
            });
            this.favoriteList = response.data.data;
            this.startWebsocket();
          }
        }
      );
    },
    getSymbol() {
      let data = {
        baseSymbol: this.currentTab.unit,
        coinSymbol: this.searchKey,
        pageSize: this.pageSize,
        pageNo: this.pageNo
      };
      MarketMarketService.getSymbolsThumbTrend(data).then(response => {
        this.items = response.data.data.content;
        this.items.forEach(item => {
          item.isFavor = this.favoriteList.some(
            fav => fav.symbol === item.symbol
          );
        });
        this.totalPages = response.data.data.totalPages;
        this.startWebsocket();
      });
    },
    getBaseSymbols() {
      MarketMarketService.getBaseSymbolsList().then(response => {
        this.baseCoinType = response.data.data;
        if (!this.isAuth) {
          this.currentTab = response.data.data[0];
          this.getSymbol();
        }
      });
    },
    startWebsocket() {
      // unsubscribe last subscribe
      this.subscriptions.forEach(sub => sub.unsubscribe());
      this.subscriptions = [];

      let stompClient = null;
      let socket = new SockJS(
        `${process.env.VUE_APP_BASE_MARKET}/market/market-ws`
        // `https://market-api.excoino.com/market/market-ws`
      );
      stompClient = Stomp.over(socket);
      stompClient.debug = false;
      stompClient.connect({}, () => {
        //subscribe to price change news
        this.items.forEach(item => {
          const subscription = stompClient.subscribe(
            `/topic/market/thumb/${item.symbol}`,
            function(msg) {
              let resp = JSON.parse(msg.body);
              item.close = resp.close;
              item.chg = resp.chg;
              item.high = resp.high;
              item.low = resp.low;
              item.twentyFourHourTurnover = resp.twentyFourHourTurnover;
              item.volume = parseInt(resp.volume);
            }
          );
          // push subscription for Unsubscription
          this.subscriptions.push(subscription);
        });
      });
    }
  }
};
</script>

<style lang="scss">
.market-tabs {
  .v-item-group {
    .v-slide-group__prev,
    .v-slide-group__next {
      display: none !important;
    }
  }
}
</style>
